import { useCallback, useEffect, useRef, useState } from "react";

import {
  useLazyGetAppointmentQuery,
  useZoomSyncAppointmentMutation
} from "services/appointments";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 10;

export function useZoomLinkGeneration(appointmentId: number | undefined) {
  const [zoomSyncAppointment, { isLoading: isZoomSyncing }] =
    useZoomSyncAppointmentMutation();
  const [fetchAppointment] = useLazyGetAppointmentQuery();
  const [isPollingZoom, setIsPollingZoom] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleGenerateZoomLink = useCallback(async () => {
    if (!appointmentId) return;
    try {
      await zoomSyncAppointment(appointmentId).unwrap();
      setIsPollingZoom(true);
      let attempts = 0;
      pollIntervalRef.current = setInterval(async () => {
        attempts++;
        const result = await fetchAppointment({
          id: appointmentId
        }).unwrap();
        if (result?.zoom_join_url || attempts >= MAX_POLL_ATTEMPTS) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPollingZoom(false);
        }
      }, POLL_INTERVAL_MS);
    } catch {
      // Error handled by RTK Query
    }
  }, [appointmentId, zoomSyncAppointment, fetchAppointment]);

  return {
    handleGenerateZoomLink,
    isZoomGenerating: isZoomSyncing || isPollingZoom
  };
}
